import { FC, forwardRef, useImperativeHandle, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { showInvalidFieldMsg } from '@/common/utils/ways';
import { postFranchiseeTransfer } from 'src/common/api/expandStore/franchisee';
import { Form, Modal } from 'antd';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import V2Form from '@/common/components/Form/V2Form';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import CompanyMember from 'src/common/components/business/DynamicComponent/components/CompanyMember';

/**
 * 加盟商-移交
 * @description 支持单条移交（详情页）与批量移交（列表页勾选后操作），两者仅接口入参不同
 * @demo
 */
const Component:FC<{
  /** 加盟商id（单条移交时传入） */
  franchiseeId?: number,
  /** 移交成功回调 */
  onSuccess?: any,
  ref?: any,
}> = forwardRef(({ franchiseeId, onSuccess }, ref) => {

  useImperativeHandle(ref, () => ({
    init: methods.init,
  }));

  /* state */
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);
  /** 本次移交的加盟商ID列表（批量移交时包含多条） */
  const [targetIds, setTargetIds] = useState<number[]>([]);
  /** 是否批量移交：由 init 时是否传入 franchiseeIds 决定 */
  const [isBatch, setIsBatch] = useState<boolean>(false);

  /* hooks */

  /* methods */
  const methods = useMethods({

    /**
     * @description 打开弹窗
     * @param options.franchiseeIds 批量移交的加盟商ID列表，不传则为单条移交
     */
    init(options?: { franchiseeIds?: number[] }) {
      const franchiseeIds = options?.franchiseeIds || [];
      const batch = franchiseeIds.length > 0;
      setIsBatch(batch);
      setTargetIds(batch ? franchiseeIds : (franchiseeId ? [franchiseeId] : []));
      setVisible(true);
      form.resetFields();

    },
    close() {
      setVisible(false);
    },

    confirm() {
      form.validateFields().then((values) => {
        // console.log('values', values);
        // 这里抛出的是对象数组
        const affiliationItem = Array.isArray(values.franchiseeAffiliation) && values.franchiseeAffiliation.length ? values.franchiseeAffiliation[0] : null;
        const affiliation = affiliationItem?.value;
        if (!affiliation) {
          V2Message.error('请选择归属人');
          return;
        }

        V2Confirm({
          content: methods.getConfirmContent(affiliationItem?.label),
          onSure() {
            if (requesting) {
              return;
            }

            const params = methods.getRequestParams(affiliation);

            setRequesting(true);
            postFranchiseeTransfer(params).then(() => {
              V2Message.success('移交成功');
              setVisible(false);
              onSuccess();
            }).finally(() => {
              setRequesting(false);
            });
          }
        });
      }).catch((err) => {
        err && showInvalidFieldMsg(err.errorFields, 1);
      });
    },

    /**
     * @description 二次确认文案：批量移交提示同步转移的任务与踩点记录，单条移交沿用原有文案
     * @param affiliationName 归属人姓名
     */
    getConfirmContent(affiliationName?: string) {
      if (!isBatch) return '确定操作吗？';
      return `确认将所选 ${targetIds.length} 个加盟商移交给「${affiliationName || ''}」？ 移交后其名下进行中的拓店任务、踩点记录将同步转移，且你不再拥有这些加盟商的操作权限。`;
    },

    /**
     * @description 组装移交接口入参：批量移交传 franchiseeIds，单条移交传 franchiseeId
     * @param affiliation 归属人ID
     */
    getRequestParams(affiliation: number) {
      if (isBatch) {
        return {
          franchiseeIds: targetIds,
          affiliation,
        };
      }
      return {
        franchiseeId,
        affiliation,
      };
    },

  });

  return (<Modal
    title={isBatch ? `批量移交（共${targetIds.length}条）` : '移交'}
    width={420}
    open={visible}
    destroyOnClose
    onCancel={methods.close}
    onOk={methods.confirm}
    confirmLoading={requesting}
  >
    <V2Form form={form}>
      <CompanyMember
        form={form}
        propertyItem={{
          identification: 'franchiseeAffiliation',
          name: '归属人',
        }}
        required={true}
        disabled={false}
        isEdit={true}
      />
    </V2Form>
  </Modal>);
});

export default Component;
